/* eslint-disable @typescript-eslint/no-explicit-any */
import { StyleProvider } from '@ant-design/cssinjs';
import '@sass/layout.scss';
import {
    MutationCache,
    QueryCache,
    QueryClient,
    QueryClientProvider,
    matchQuery,
    type Query,
} from '@tanstack/react-query';
import { App as AppWrapper, ConfigProvider } from 'antd';
import { useState } from 'react';
import { RouterProvider } from 'react-router/dom';
import router from './routes';
import { fn } from './utils';
import { url } from '@app/utils/constants';

const antdTheme = {
    cssVar: { prefix: 'app' },
    token: {
        fontFamily: 'inherit',
        fontSize: 16,
    },
    components: {
        Table: {
            selectionColumnWidth: 60,
        },
    },
};

const handleGlobalError = (error: any) => {
    const wasLoggedIn = fn.isLoggedIn();
    if (error?.response?.status === 401) {
        fn.clearAllData();
        if (wasLoggedIn) {
            window.location.href = url.sessionExpired;
        } else {
            window.location.href = url.home;
        }
    }
    if (error?.response?.status === 429) {
        window.location.href = url.tooManyRequests;
    }
};

const handleInvalidateQueries = (query: Query, invalidates?: boolean | string[][]) => {
    if (invalidates === undefined || invalidates === null) return false;
    if (typeof invalidates === 'boolean') return invalidates;

    return invalidates?.some((queryKey) => matchQuery({ queryKey }, query)) ?? true;
};

const mutationCache = new MutationCache({
    onSuccess: async (_data, _variables, _context, mutation) => {
        try {
            await queryClientDefaults.invalidateQueries({
                predicate: (q: any) =>
                    handleInvalidateQueries(q, mutation.meta?.invalidates as boolean | string[][]),
            });
        } catch (e) {
            console.error('Error invalidating queries after mutation success', e);
        }
    },
    onError: (error, _variables, _context, mutation) => {
        if (mutation?.meta?.skipGlobalErrorHandling) {
            return;
        }

        handleGlobalError(error);
    },
});

const queryCache = new QueryCache({
    onError: (error, query) => {
        if (query?.meta?.skipGlobalErrorHandling) {
            return;
        }

        handleGlobalError(error);
    },
});

const queryClientDefaults = new QueryClient({
    mutationCache,
    queryCache,
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: false,
        },
    },
});

function App() {
    const [queryClient] = useState(() => queryClientDefaults);

    return (
        <QueryClientProvider client={queryClient}>
            <StyleProvider layer>
                <ConfigProvider theme={antdTheme}>
                    <AppWrapper>
                        <RouterProvider router={router} />
                    </AppWrapper>
                </ConfigProvider>
            </StyleProvider>
        </QueryClientProvider>
    );
}

export default App;
